export interface PipePoint {
  x: number
  y: number
  z: number
}

export interface PipeObjectLike {
  id: string
  params: {
    waypoints?: PipePoint[]
    diameter?: number
    color?: string
  }
}

export type PipeFittingKind = 'elbow' | 'tee' | 'cross'

export interface PipeFitting {
  kind: PipeFittingKind
  x: number
  y: number
  z: number
  yawDeg: number
}

interface Segment {
  pipeId: string
  from: PipePoint
  to: PipePoint
  segIndex: number
}

interface NodeInfo {
  x: number
  y: number
  z: number
  pipes: Set<string>
  dirs: { x: number; z: number }[]
}

const EPS = 1e-6
const KEY_PRECISION = 100

function nodeKey(x: number, z: number) {
  return `${Math.round(x * KEY_PRECISION)}:${Math.round(z * KEY_PRECISION)}`
}

function normalizeDir(x: number, z: number) {
  const l = Math.hypot(x, z)
  if (l < EPS) return { x: 0, z: 0 }
  return { x: x / l, z: z / l }
}

function segIntersection2D(a1: PipePoint, a2: PipePoint, b1: PipePoint, b2: PipePoint) {
  const dax = a2.x - a1.x
  const daz = a2.z - a1.z
  const dbx = b2.x - b1.x
  const dbz = b2.z - b1.z
  const den = dax * dbz - daz * dbx
  if (Math.abs(den) < EPS) return null
  const ax = b1.x - a1.x
  const az = b1.z - a1.z
  const ta = (ax * dbz - az * dbx) / den
  const tb = (ax * daz - az * dax) / den
  if (ta < -EPS || ta > 1 + EPS || tb < -EPS || tb > 1 + EPS) return null
  return {
    x: a1.x + dax * ta,
    z: a1.z + daz * ta,
  }
}

function uniqueDirs(dirs: { x: number; z: number }[]) {
  const out: { x: number; z: number }[] = []
  for (const d of dirs) {
    let dup = false
    for (const e of out) {
      if (Math.abs(d.x - e.x) < 1e-3 && Math.abs(d.z - e.z) < 1e-3) {
        dup = true
        break
      }
    }
    if (!dup) out.push(d)
  }
  return out
}

export function buildPipeTopology(pipes: PipeObjectLike[]) {
  const segments: Segment[] = []
  for (const p of pipes) {
    const wps = p.params.waypoints ?? []
    for (let i = 0; i < wps.length - 1; i++) {
      segments.push({ pipeId: p.id, from: wps[i]!, to: wps[i + 1]!, segIndex: i })
    }
  }

  const nodeMap = new Map<string, NodeInfo>()
  const ensureNode = (x: number, y: number, z: number) => {
    const k = nodeKey(x, z)
    let n = nodeMap.get(k)
    if (!n) {
      n = { x, y, z, pipes: new Set<string>(), dirs: [] }
      nodeMap.set(k, n)
    }
    return n
  }

  for (const s of segments) {
    const n1 = ensureNode(s.from.x, s.from.y, s.from.z)
    n1.pipes.add(s.pipeId)
    n1.dirs.push(normalizeDir(s.to.x - s.from.x, s.to.z - s.from.z))
    const n2 = ensureNode(s.to.x, s.to.y, s.to.z)
    n2.pipes.add(s.pipeId)
    n2.dirs.push(normalizeDir(s.from.x - s.to.x, s.from.z - s.to.z))
  }

  for (let i = 0; i < segments.length; i++) {
    for (let j = i + 1; j < segments.length; j++) {
      const a = segments[i]!
      const b = segments[j]!
      if (a.pipeId === b.pipeId && Math.abs(a.segIndex - b.segIndex) <= 1) continue
      const hit = segIntersection2D(a.from, a.to, b.from, b.to)
      if (!hit) continue
      const y = (a.from.y + a.to.y + b.from.y + b.to.y) / 4
      const n = ensureNode(hit.x, y, hit.z)
      n.pipes.add(a.pipeId)
      n.pipes.add(b.pipeId)
      n.dirs.push(normalizeDir(a.to.x - a.from.x, a.to.z - a.from.z))
      n.dirs.push(normalizeDir(a.from.x - a.to.x, a.from.z - a.to.z))
      n.dirs.push(normalizeDir(b.to.x - b.from.x, b.to.z - b.from.z))
      n.dirs.push(normalizeDir(b.from.x - b.to.x, b.from.z - b.to.z))
    }
  }

  const fittingsByPipe = new Map<string, PipeFitting[]>()
  const nodeByPipe = new Map<string, PipePoint[]>()
  for (const p of pipes) {
    fittingsByPipe.set(p.id, [])
    nodeByPipe.set(p.id, [])
  }

  for (const node of nodeMap.values()) {
    const dirs = uniqueDirs(node.dirs)
    const degree = dirs.length
    if (degree < 2) continue

    let kind: PipeFittingKind | null = null
    if (degree >= 4) kind = 'cross'
    else if (degree === 3) kind = 'tee'
    else {
      const d0 = dirs[0]!
      const d1 = dirs[1]!
      const dot = d0.x * d1.x + d0.z * d1.z
      if (Math.abs(dot) < 0.96) kind = 'elbow'
    }
    if (!kind) continue

    const yaw = Math.atan2(dirs[0]!.z, dirs[0]!.x) * 180 / Math.PI
    for (const pid of node.pipes) {
      fittingsByPipe.get(pid)?.push({
        kind,
        x: node.x,
        y: node.y,
        z: node.z,
        yawDeg: yaw,
      })
      nodeByPipe.get(pid)?.push({ x: node.x, y: node.y, z: node.z })
    }
  }

  return { fittingsByPipe, nodeByPipe }
}

export function toPipeObjects(input: { id: string; params: Record<string, any> }[]) {
  return input
    .filter(o => o.params && Array.isArray(o.params.waypoints))
    .map(o => ({
      id: o.id,
      params: {
        waypoints: (o.params.waypoints as PipePoint[]).map(p => ({ x: Number(p.x), y: Number(p.y), z: Number(p.z) })),
        diameter: Number(o.params.diameter ?? 0.35),
        color: String(o.params.color ?? '#9ca3af'),
      },
    }))
}
